import React from "react";
import LegalPageShell from "./LegalPageShell";
import {
  ATTESTATION_TEXT,
  BRAND_NAME,
  CORE_DISCLAIMER,
  FRAUD_WARNING,
  GOVERNING_STATE,
  LEGAL_EMAIL,
  LEGAL_ENTITY_NAME,
  MAILING_ADDRESS,
  POLICIES_LAST_UPDATED,
  REFUND_WINDOW_DAYS,
  SITE_DOMAIN,
  SUPPORT_EMAIL,
  SUPPORT_PHONE,
} from "constants/business-info";
import { MAX_STUBS, MIN_STUBS, UNIT_PRICE } from "constants/pricing";

const PRICE = `$${UNIT_PRICE.toFixed(2)}`;

const TermsOfUse = () => (
  <LegalPageShell>
    <h2>Terms of Use</h2>
    <p><strong>Last Updated:</strong> {POLICIES_LAST_UPDATED}</p>
    <p>
      These Terms of Use ("Terms") govern your access to and use of {SITE_DOMAIN} and the software we
      make available on it (the "Service"). The Service is operated by {LEGAL_ENTITY_NAME} ("we," "us,"
      or "our"). By using the Service, you agree to these Terms. If you do not agree, do not use the
      Service.
    </p>

    <h4>1. Nature of the Service and Your Responsibility</h4>
    <p>
      <strong>Read this section before you use the Service.</strong> It is the most important part of
      these Terms.
    </p>
    <p>
      <strong>{CORE_DISCLAIMER}</strong>
    </p>
    <p>
      <strong>{FRAUD_WARNING}</strong>
    </p>
    <p>
      You are the author of every document you create with the Service. You supply every figure. You
      are the sole warrantor of the contents of the document. We are a formatting tool: we lay out the
      numbers you type into a conventional pay stub format and run standard arithmetic on them. We are
      not a payroll provider, an accountant, a bookkeeper, or a tax advisor, and nothing on the Service
      is accounting, tax, or legal advice.
    </p>
    <p>
      We do not verify any figure you enter. We do not check that an employer exists, that an employment
      relationship exists, that hours were worked, or that wages were paid. Every document you create must
      reflect real wages actually earned and actually paid. If it does not, you must not create it, download
      it, print it, or give it to anyone.
    </p>
    <p>
      We keep no copy of any document created with the Service. For that reason we cannot confirm, to you or
      to anyone else, whether a particular document was produced by the Service or what it contained.
    </p>

    <h4>2. Agreement and Accuracy Attestation</h4>
    <p>
      Before you can pay for and download a document, you must tick a box at checkout confirming that you have
      read and agree to these Terms and our Privacy Policy, and attesting to the accuracy of the document. The
      attestation reads:
    </p>
    <p>
      <em>"{ATTESTATION_TEXT}"</em>
    </p>
    <p>
      That attestation is a condition of the license we grant you to use any document generated by the
      Service. If the attestation is false when you make it, the license is void, you have no right to use
      or present the document for any purpose, and you are in breach of these Terms.
    </p>
    <p>
      We record the time you ticked the box, the version of these Terms in force at that moment, and the IP
      address and browser from which you did so, and we keep that record with your order.
    </p>

    <h4>3. What the Service Is</h4>
    <p>
      <strong>The Service</strong><br />
      {BRAND_NAME} is a self-serve document formatting tool for small businesses, freelancers, and
      independent contractors who need to produce a pay stub from figures they already have. You enter
      company details, employee details, a pay period, and earnings. The Service calculates withholding
      using standard published federal and state rates, renders a pay stub, and lets you download it as a
      PDF or print it.
    </p>
    <p>
      <strong>Formatting, Not Certification</strong><br />
      The documents produced by the Service follow conventional US pay stub formatting and include the line
      items commonly found on wage records: gross pay, earnings breakdown, taxes withheld, deductions, net
      pay, and year-to-date figures. <strong>We make no representation that any document produced by the
      Service satisfies any specific federal, state, or local requirement</strong>, or that any third party
      will accept it. Recordkeeping and pay stub rules vary by jurisdiction and change over time. You are
      responsible for satisfying yourself that a document you create meets whatever requirement you are
      trying to meet.
    </p>
    <p>
      <strong>What the Service Is Not</strong><br />
      The Service does not file anything with the IRS or any state agency. It does not remit taxes. It does
      not produce W-2s, 1099s, or any other tax form. It does not run payroll, pay employees, or keep payroll
      history. Year-to-date figures are estimates derived from the figures you enter, not a record of what was
      actually paid across the year.
    </p>

    <h4>4. Acceptance of Terms</h4>
    <p>
      <strong>Agreement</strong><br />
      By using the Service, you acknowledge that you have read, understood, and agree to be bound by these
      Terms and by our Privacy Policy. These Terms are a legally binding agreement between you and{" "}
      {LEGAL_ENTITY_NAME}.
    </p>
    <p>
      <strong>Age and Capacity</strong><br />
      You must be at least 18 years old and legally able to enter into a contract. If you use the Service on
      behalf of a business or organization, you represent that you are authorized to bind that entity to these
      Terms.
    </p>
    <p>
      <strong>Changes to These Terms</strong><br />
      We may change these Terms. When we do, we will update the "Last Updated" date above and post the revised
      Terms on this page. Your continued use of the Service after we post a change means you accept it. We do
      not use the email address PayPal gives us to send notices or marketing, so please check this page from
      time to time.
    </p>

    <h4>5. No Account Required</h4>
    <p>
      There are no accounts on {SITE_DOMAIN}. There is no registration, no login, no password, no dashboard,
      and no order history. You do not create an account, so there is no account for you to secure, for us to
      suspend, or for either of us to close.
    </p>
    <p>
      Your pay stub data stays in your own browser and is never sent to us. What does reach our server is
      limited to your order (how many stubs, the price, any discount code, the template you chose, and your
      checkout confirmation), PayPal's confirmation of your payment, and anonymous usage statistics, all
      described in section 9 and in our Privacy Policy. Because we hold no copy of your document, we cannot
      retrieve, re-send, or re-issue it after you leave the site. Download or print your document before you
      close the tab, and keep your own copy.
    </p>

    <h4>6. Acceptable Use</h4>
    <p>
      <strong>Permitted Use</strong><br />
      You may use the Service only to create wage records that are true. That means: pay stubs for your own
      business documenting wages you actually paid, or pay stubs documenting wages you were actually paid, or
      pay stubs you are otherwise authorized to prepare as an employer, employee, or authorized representative.
    </p>
    <p>
      <strong>Prohibited Use</strong><br />
      You may not use the Service to:
    </p>
    <ul>
      <li>Create a fraudulent, deceptive, forged, or misleading pay stub.</li>
      <li>Enter false or inflated income, hours, tax, employer, or employee information.</li>
      <li>
        Misrepresent income to a lender, mortgage broker, landlord, employer, insurer, government agency, or
        court — including loan fraud, rental application fraud, benefits fraud, and immigration fraud.
      </li>
      <li>Evade taxes or misstate wages to any tax authority.</li>
      <li>Commit identity theft or create a document in the name of a person who has not authorized it.</li>
      <li>Document an employment relationship that does not exist, or wages that were never paid.</li>
      <li>
        Present a document you created here as having been issued by an employer or payroll provider when it
        was not.
      </li>
      <li>Resell, redistribute, or offer documents produced by the Service as a service to third parties.</li>
      <li>Reverse-engineer, scrape, overload, or otherwise interfere with the Service.</li>
      <li>Break any applicable federal, state, or local law.</li>
    </ul>
    <p>
      <strong>This is not a formality.</strong> Falsifying a pay stub to obtain money, credit, housing, or a
      benefit is a serious offense and can be prosecuted as fraud. We will cooperate with law enforcement and
      respond to valid legal process. We may block access to the Service by anyone we reasonably believe is
      using it for a prohibited purpose.
    </p>
    <p>
      <strong>Content Standards</strong><br />
      Every value you enter must be accurate, truthful, and complete. You warrant that it is. You are responsible
      for complying with all laws that apply to you, including wage and hour law, tax withholding rules, and
      employer recordkeeping obligations.
    </p>

    <h4>7. Intellectual Property</h4>
    <p>
      <strong>Ours</strong><br />
      The Service — including its software, templates, layout, text, graphics, and logos — is owned by{" "}
      {LEGAL_ENTITY_NAME} and protected by copyright, trademark, and other intellectual property laws. Our name
      and marks may not be used without our written permission.
    </p>
    <p>
      <strong>Yours</strong><br />
      The pay stub data you enter is yours. It never reaches us, so we acquire no rights in it and take no
      license to it.
    </p>
    <p>
      <strong>Documents You Generate</strong><br />
      Subject to your accuracy attestation and to these Terms, we grant you a license to use, print, and share
      the documents you generate for their legitimate purpose. You may not use our templates or formatting to
      build a competing document generation product or to produce documents commercially for third parties.
    </p>

    <h4>8. Pricing, Payment, and Refunds</h4>
    <p>
      <strong>Price</strong><br />
      The Service is charged per pay stub. The price is {PRICE} for each pay stub you generate. You may generate
      between {MIN_STUBS} and {MAX_STUBS} pay stubs in a single order, and you are charged {PRICE} for each one.
      The total is shown to you before you pay. There is no subscription and no recurring charge.
    </p>
    <p>
      <strong>Discount Codes</strong><br />
      We may offer percentage discount codes from time to time. A code applies only if you enter it at checkout
      before you pay, one code per order, and a discount is never applied after payment. Codes have no cash
      value, may be changed or withdrawn at any time, and are validated by our server, whose decision is final.
    </p>
    <p>
      <strong>Payment</strong><br />
      Payment is by PayPal only. PayPal processes the transaction. We never see, receive, or store your card
      number, bank details, or PayPal credentials. When your payment completes, PayPal sends us a confirmation
      that includes the amount, the PayPal order id, and the name and email address on your PayPal account. We
      keep that confirmation as the record of your order, as described in our Privacy Policy. Your payment is
      governed by PayPal's own terms and privacy policy.
    </p>
    <p>
      <strong>Delivery</strong><br />
      Once payment succeeds, the Service unlocks the download and print functions in your browser. The document is
      delivered as a PDF download or a browser print. Nothing is emailed to you and nothing is stored for later
      retrieval. Save your file before you close the tab.
    </p>
    <p>
      <strong>{REFUND_WINDOW_DAYS}-Day Satisfaction Guarantee</strong><br />
      We offer a {REFUND_WINDOW_DAYS}-day satisfaction guarantee. If, within {REFUND_WINDOW_DAYS} days of your
      purchase, the document is defective — it will not download or print, it is corrupted, or our calculations are
      wrong — email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and we will refund you in full. You do
      not have to argue with us about it.
    </p>
    <p>
      If the problem is an error in the data you entered, a refund is at our discretion — but we will always give
      you a free corrected document. Just tell us what was wrong.
    </p>
    <p>
      <strong>Billing Disputes</strong><br />
      If you believe you have been charged in error, contact us at{" "}
      <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> within 30 days of the charge. We will investigate
      promptly and correct any genuine error.
    </p>

    <h4>9. Your Pay Stub Data Stays in Your Browser</h4>
    <p>
      The pay stub builder runs entirely in your browser. The company details, employee details, Social Security
      or Employer Identification numbers, bank routing and account numbers, and wage figures you enter are held
      in your browser's memory and in a local draft saved on your own device. <strong>They are never transmitted
      to us.</strong> Our server handles payment and anonymous usage statistics only. It never receives pay stub
      data, and we hold no copy of your document.
    </p>
    <p>
      The site is served over HTTPS, which encrypts traffic between your browser and our server in transit. That
      protects the delivery of the website and of your payment requests. It is not a claim about data we hold,
      because we hold no pay stub data.
    </p>
    <p>
      Because your data lives on your device, protecting the device is your responsibility. Clearing the draft or
      your browser storage deletes it. Our{" "}
      <a href={`https://${SITE_DOMAIN}/privacy`} target="_blank" rel="noopener noreferrer">Privacy Policy</a>{" "}
      explains this in full and is incorporated into these Terms.
    </p>

    <h4>10. Service Availability</h4>
    <p>
      We aim to keep the Service available, but we do not guarantee uninterrupted access. The Service may be
      unavailable for maintenance, updates, or reasons outside our control. We may change, suspend, or discontinue
      any part of the Service at any time.
    </p>
    <p>
      Support is available by email at <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and by phone at{" "}
      {SUPPORT_PHONE}. We respond during business hours.
    </p>

    <h4>11. Disclaimer of Warranties</h4>
    <p>
      The Service is provided "as is" and "as available," without warranty of any kind, express or implied. To the
      fullest extent permitted by law, we disclaim all warranties, including the implied warranties of
      merchantability, fitness for a particular purpose, and non-infringement.
    </p>
    <p>
      We do not warrant that any document you produce will be accepted by any lender, landlord, employer, agency,
      or court, or that it will satisfy any legal or regulatory requirement. Tax calculations are based on
      published rates and standard formulas and are an aid, not a substitute for professional advice. You are
      responsible for the accuracy and appropriateness of everything in your document.
    </p>

    <h4>12. Limitation of Liability</h4>
    <p>
      To the fullest extent permitted by law, {LEGAL_ENTITY_NAME} will not be liable for any indirect, incidental,
      special, consequential, or punitive damages, including lost profits, lost data, or lost business
      opportunities, arising out of or relating to the Service.
    </p>
    <p>
      Our total liability for all claims arising out of or relating to the Service will not exceed the total
      amount you paid us in the twelve months before the claim arose.
    </p>
    <p>
      We are not liable for any consequence of a document that contains information you entered incorrectly, or
      for any use you make of a document in breach of these Terms.
    </p>

    <h4>13. Indemnification</h4>
    <p>
      You agree to indemnify, defend, and hold harmless {LEGAL_ENTITY_NAME} and its officers, directors,
      employees, and agents from any claim, liability, damage, loss, cost, or expense (including reasonable legal
      fees) arising out of your use of the Service, the contents of any document you create, your breach of these
      Terms, or your violation of any law or third-party right. We may assume the exclusive defense of any matter
      subject to indemnification by you, and you agree to cooperate. This obligation survives your use of the
      Service.
    </p>

    <h4>14. Dispute Resolution</h4>
    <p>
      <strong>Talk to Us First</strong><br />
      Before starting any formal proceeding, contact us at{" "}
      <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. Most problems can be fixed quickly.
    </p>
    <p>
      <strong>Governing Law</strong><br />
      These Terms and any dispute arising out of or relating to them or to the Service are governed by the laws of
      the State of {GOVERNING_STATE}, without regard to its conflict of law rules.
    </p>
    <p>
      <strong>Courts</strong><br />
      Any dispute that is not resolved informally will be brought exclusively in the state or federal courts
      located in the State of {GOVERNING_STATE}, and you and we each consent to the personal jurisdiction of those
      courts. Either of us may instead bring an individual claim in small claims court if it qualifies.
    </p>
    <p>
      <strong>Jury Trial Waiver</strong><br />
      To the fullest extent permitted by law, you and we each waive any right to a jury trial in any proceeding
      arising out of or relating to these Terms or the Service.
    </p>
    <p>
      <strong>Class Action Waiver</strong><br />
      Any proceeding will be limited to the dispute between you and {LEGAL_ENTITY_NAME} individually. To the
      fullest extent permitted by law, you waive any right to participate in a class action against us.
    </p>

    <h4>15. Suspension of Access</h4>
    <p>
      There are no accounts to terminate. We may, however, block or restrict your access to the Service at any
      time, without notice, if we reasonably believe you are using it for a prohibited purpose or in breach of
      these Terms. The sections that by their nature should survive — including Nature of the Service, Agreement
      and Accuracy Attestation, Acceptable Use, Intellectual Property, Disclaimer of Warranties, Limitation of
      Liability, Indemnification, and Dispute Resolution — survive any end of your use of the Service.
    </p>

    <h4>16. General</h4>
    <p>
      <strong>Entire Agreement</strong><br />
      These Terms, together with our Privacy Policy, are the entire agreement between you and{" "}
      {LEGAL_ENTITY_NAME} about the Service, and supersede any prior understanding.
    </p>
    <p>
      <strong>Severability</strong><br />
      If any provision of these Terms is held invalid or unenforceable, the rest remain in force, and the invalid
      provision will be narrowed only as far as needed to make it enforceable.
    </p>
    <p>
      <strong>Assignment</strong><br />
      You may not assign your rights under these Terms without our written consent. We may assign ours.
    </p>
    <p>
      <strong>Force Majeure</strong><br />
      We are not liable for any failure or delay caused by circumstances beyond our reasonable control.
    </p>
    <h4>Acknowledgment</h4>
    <p>
      By using {SITE_DOMAIN}, you acknowledge that you have read and understood these Terms, that you are solely
      responsible for the accuracy of every figure you enter, and that documents you create must reflect wages
      genuinely earned and paid.
    </p>
    <p>
      <strong>Effective Date:</strong> These Terms are effective as of {POLICIES_LAST_UPDATED}.<br />
      The current version is always at{" "}
      <a href={`https://${SITE_DOMAIN}/terms`} target="_blank" rel="noopener noreferrer">
        {SITE_DOMAIN}/terms
      </a>
      .
    </p>

    <h4>Contact</h4>
    <p>
      Questions about these Terms:<br />
      Email: <a href={`mailto:${LEGAL_EMAIL}`}>{LEGAL_EMAIL}</a><br />
      Support: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a><br />
      Mailing address: {MAILING_ADDRESS}<br />
      Phone: {SUPPORT_PHONE}
    </p>
  </LegalPageShell>
);

export default TermsOfUse;
